<template>
  <v-data-table
    :headers="headers"
    :items="categories"
    :class="tableClass"
    :rows-per-page-items="[10, 15, 25]"
    :no-data-text="$t('dataTable.noResult')"
    hide-actions
    disable-initial-sort
  >
    <!-- Sólo se personaliza la cabecera cuando la fila necesita fondo propio
         (ampliaciones rechazadas); si no, se respeta la cabecera por defecto. -->
    <template v-if="rowClass" v-slot:headers="props">
      <tr class="v-table theme--light">
        <th v-for="header in props.headers" :key="header.text" :class="rowClass">
          {{ header.text }}
        </th>
      </tr>
    </template>

    <template v-slot:items="props">
      <tr :class="rowClass">
        <td class="text-sm-left text-truncate" style="max-width: 160px">
          <v-autocomplete
            :value="categoryValue(props.item)"
            name="categoryId"
            :items="categoryOptions"
            :rules="rules.required"
            :disabled="readonly"
            class="text-sm-center"
            item-text="name"
            item-value="id"
            color="primary"
            hide-details
            chips
            small-chips
            @input="props.item.categoryId = $event"
          />
        </td>
        <td>
          <v-text-field
            v-model="props.item.price"
            name="price"
            :hide-details="true"
            :rules="positiveNumberRules"
            :disabled="readonly"
            class="text-sm-center"
            color="primary"
            type="number"
            min="0"
            outlined
            dense
            @change="recalculate(props.item)"
          />
        </td>
        <td>
          <v-text-field
            v-model="props.item.quantity"
            name="quantity"
            :hide-details="true"
            :rules="positiveNumberRules"
            :disabled="readonly"
            class="text-sm-center"
            color="primary"
            type="number"
            min="0"
            outlined
            dense
            @change="recalculate(props.item)"
          />
        </td>
        <td>
          <v-textarea
            v-model="props.item.notes"
            name="notes"
            :hide-details="true"
            :disabled="readonly"
            class="text-sm-center"
            color="primary"
            rows="1"
            auto-grow
            outlined
            dense
          />
        </td>
        <td>
          <v-text-field
            v-model="props.item.peopleCount"
            name="peopleCount"
            :hide-details="true"
            :rules="positiveNumberRules"
            :disabled="readonly"
            class="text-sm-center"
            color="primary"
            type="number"
            min="0"
            outlined
            dense
          />
        </td>
        <td>
          <date-field
            v-model="props.item.startDate"
            name="startDate"
            :label="$t('general.startDate')"
            :min="minDate"
            :max="props.item.endDate || maxDate"
            :disabled="readonly || !minDate || !maxDate"
            prepend-icon=""
            input-class="pt-0 mt-0"
            hide-details
            clearable
            required
          />
        </td>
        <td>
          <date-field
            v-model="props.item.endDate"
            name="endDate"
            :label="$t('general.endDate')"
            :min="props.item.startDate || minDate"
            :max="maxDate"
            :disabled="readonly || !minDate || !maxDate"
            prepend-icon=""
            input-class="pt-0 mt-0"
            hide-details
            clearable
            required
          />
        </td>
        <td>
          <v-text-field
            v-model="props.item.amount"
            name="amount"
            :hide-details="true"
            :rules="rules.required"
            :disabled="readonly"
            class="text-sm-center mb-1"
            color="primary"
            type="number"
            min="0"
            outlined
            dense
          />
        </td>
        <td>
          <v-btn :disabled="readonly" icon @click="$emit('remove', props.index)">
            <v-icon color="red">mdi-delete</v-icon>
          </v-btn>
        </td>
      </tr>
    </template>

    <template v-slot:footer>
      <tr :class="rowClass">
        <td colspan="6" class="text-sm-right">
          <span class="h6 primary--text text-uppercase font-weight-black">
            {{ totalLabel || $t('general.total') }}:
          </span>
        </td>
        <td colspan="2" class="primary mt-2">
          <span class="h6 font-weight-black white--text">
            {{ total | formatCurrency }} MXN
          </span>
        </td>
        <td colspan="1"></td>
      </tr>
    </template>
  </v-data-table>
</template>

<script>
import DateField from './DateField.vue'
import { sumCategories, toNumber } from './request.model'

const DISPLAY_ONLY_PREFIX = '__name__:'

export default {
  name: 'RequestCategoriesTable',
  components: { DateField },
  props: {
    /** Categorías ya normalizadas (ver buildRequestModel). */
    categories: {
      type: Array,
      default: () => []
    },
    /** Catálogo de categorías disponible para seleccionar. */
    catalog: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: true
    },
    /** Rango permitido para las fechas de cada partida. */
    minDate: {
      type: String,
      default: null
    },
    maxDate: {
      type: String,
      default: null
    },
    totalLabel: {
      type: String,
      default: ''
    },
    /** Clase aplicada a filas y cabecera (ampliaciones rechazadas). */
    rowClass: {
      type: String,
      default: ''
    },
    tableClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    rules () {
      return this.$validationRules()
    },
    positiveNumberRules () {
      return [value => (value !== null && value !== '' && Number(value) >= 0) || this.$t('validation.required')]
    },
    total () {
      return sumCategories(this.categories)
    },
    /**
     * El catálogo puede no estar cargado (o el back aún no enviarlo). En ese caso
     * seguimos mostrando el nombre que venga en la propia partida.
     */
    categoryOptions () {
      const options = this.catalog.slice()
      this.categories.forEach(category => {
        const value = this.categoryValue(category)
        if (value === null) return

        const alreadyListed = options.some(option => option.id === value)
        if (!alreadyListed) options.push({ id: value, name: category.name })
      })
      return options
    },
    headers () {
      return [
        { text: this.$t('viatics.categoryHeader'), align: 'center', value: 'name', sortable: false },
        { text: this.$t('viatics.approxPrice'), align: 'center', value: 'price', sortable: false, width: '9%' },
        { text: this.$t('viatics.abbrQuantity'), align: 'center', value: 'quantity', sortable: false, width: '4%' },
        { text: this.$t('general.description'), align: 'center', value: 'notes', sortable: false, width: '18%' },
        { text: this.$t('viatics.abbrNumberPeople'), align: 'center', value: 'peopleCount', sortable: false, width: '3%' },
        { text: this.$t('viatics.dateStart'), align: 'center', value: 'startDate', sortable: false, width: '150px' },
        { text: this.$t('viatics.dateEnd'), align: 'center', value: 'endDate', sortable: false, width: '150px' },
        { text: this.$t('general.amount'), align: 'center', value: 'amount', sortable: false, width: '9%' },
        { text: this.$t('general.actions'), align: 'center', value: 'actions', sortable: false, width: '4%' }
      ]
    }
  },
  methods: {
    /**
     * Valor enlazado al selector. El reporte manda las categorías como
     * { nombre: monto }, sin id: en ese caso se usa un id sintético a partir del
     * nombre para poder mostrarlo, sin ensuciar `categoryId` del modelo.
     */
    categoryValue (category) {
      if (category.categoryId !== null && category.categoryId !== undefined) {
        return category.categoryId
      }
      return category.name ? DISPLAY_ONLY_PREFIX + category.name : null
    },
    recalculate (category) {
      category.amount = toNumber(category.price) * toNumber(category.quantity)
      this.$emit('change', category)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/requests/RequestCategoriesTable.vue